import { useMemo } from "react"
import { isEnabled } from "lib/@components/feature"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { resolveTokenLabels } from "authoring/routes/macro/controller/resolve-token-labels"
import { FEATURE_FLAG_COMPONENT_MACROS } from "authoring/routes/macro/feature-flag-component-macros"

const COMPONENT_PREFIX = "cmp:"

/**
 * Resolves token labels for a set of token IDs. Returns a lookup of
 * { tokenId: { id, name, label, content } } that can be merged
 * into a macroMap for display.
 *
 * Only queries the server when component tokens are present and the
 * component-macros feature flag is enabled. Uses IS_LOADING sentinel
 * to avoid flicker on cache re-fetches.
 */
export function useTokenLabels(tokenIds = []) {
    const featureEnabled = isEnabled(FEATURE_FLAG_COMPONENT_MACROS)
    const componentTokenIds = useMemo(
        () => (featureEnabled ? [...new Set(tokenIds.filter((id) => id.startsWith(COMPONENT_PREFIX)))] : []),
        [tokenIds, featureEnabled]
    )

    const resolved = resolveTokenLabels.useResults({ tokenIds: componentTokenIds }, withInitialValue(IS_LOADING))

    const loading = componentTokenIds.length > 0 && !haveResolvedRequestedTokens(componentTokenIds, resolved)
    const tokenLabelMap = useMemo(buildTokenLabelMap, [resolved])
    const error = resolved instanceof Error ? resolved : null

    return { tokenLabelMap, hasComponentTokens: componentTokenIds.length > 0, loading, error }

    function buildTokenLabelMap() {
        if (!Array.isArray(resolved)) return {}
        const map = {}
        for (const entry of resolved) {
            if (!entry?.tokenId) continue
            const label = entry.label ?? entry.tokenId
            map[entry.tokenId] = {
                id: entry.tokenId,
                name: label,
                label,
                content: entry.content ?? "",
                broken: !entry.found,
            }
        }
        return map
    }
}

function haveResolvedRequestedTokens(componentTokenIds, resolved) {
    if (!componentTokenIds.length) return true
    if (resolved === IS_LOADING) return false
    if (!Array.isArray(resolved)) return false

    const resolvedTokenIds = new Set(resolved.map((entry) => entry?.tokenId).filter(Boolean))

    return componentTokenIds.every((tokenId) => resolvedTokenIds.has(tokenId))
}
